"use client"

import type { VariantProps } from "class-variance-authority"
import type { CSSProperties, FC, ReactNode, SVGProps } from "react"
import * as React from "react"

import { cn } from "@productlane/lib/src/browser"

import type { IconType } from "../icons"
import { buttonVariants, sharedVariantStyles } from "./button-variants"
import { Loader } from "./loader"

export { buttonVariants, sharedVariantStyles }
export type { buttonVariantsType } from "./button-variants"

export interface ButtonProps
	extends
		React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	Icon?: LucideIcon | IconType
	iconRight?: LucideIcon | IconType
	command?: string
	isLoading?: boolean
	badge?: ReactNode
	badgePosition?: "top-left" | "top-right" | "bottom-left" | "bottom-right"
	iconStyle?: CSSProperties
	iconClassName?: string
	iconRightClassName?: string
	iconStrokeWidth?: number
	wrapperClassName?: string
}

export const Button = React.memo(
	React.forwardRef<HTMLButtonElement, ButtonProps>(
		(
			{
				className,
				wrapperClassName,
				iconStyle,
				iconClassName,
				iconRightClassName,
				variant,
				size,
				children,
				Icon,
				iconRight,
				isRounded,
				command,
				type = "button",
				disabled,
				isLoading = false,
				badge,
				badgePosition = "top-left",
				iconStrokeWidth = 1.75,
				...props
			},
			ref,
		) => {
			const IconRight = iconRight
			return (
				<button
					className={cn(
						buttonVariants({ variant, size, isRounded }),
						className,
					)}
					ref={ref}
					type={type}
					disabled={disabled ?? isLoading}
					{...props}
				>
					{badge ? (
						<div
							className={cn(
								"absolute rounded-lg bg-green-500 p-1 px-1.5 text-xs font-medium leading-none text-green-50",
								{
									"-left-2.5 -top-2.5": badgePosition === "top-left",
									"-right-2.5 -top-2.5": badgePosition === "top-right",
									"-bottom-2.5 -left-2.5": badgePosition === "bottom-left",
									"-bottom-2.5 -right-2.5": badgePosition === "bottom-right",
								},
							)}
						>
							{badge}
						</div>
					) : null}
					{isLoading ? (
						<div className="absolute inset-0 flex items-center justify-center">
							<Loader />
						</div>
					) : null}
					{!!Icon && (
						<Icon
							strokeWidth={iconStrokeWidth}
							className={cn(
								"h-3.5 w-3.5 flex-none",
								isLoading && "opacity-0",
								{
									"text-tertiary dark:text-secondary":
										variant === "outline" ||
										variant === "outline-subtle" ||
										variant === "editor" ||
										variant === "dashed",
									"text-tertiary dark:text-inherit":
										variant === "ghost" ||
										variant === "link" ||
										variant === "link-no-hover" ||
										variant === "glass",
									"text-secondary dark:text-inherit": variant === "subtle",
								},
								iconClassName,
							)}
							style={iconStyle}
						/>
					)}
					{(children || IconRight) && (
						<div
							className={cn(
								"flex items-center truncate",
								isLoading && "opacity-0",
								wrapperClassName,
							)}
						>
							{children}
							{!!IconRight && (
								<IconRight
									strokeWidth={iconStrokeWidth}
									className={cn(
										"ml-1 h-3 w-3 flex-none",
										isLoading && "opacity-0",
										{
											"text-tertiary dark:text-secondary":
												variant === "outline" ||
												variant === "outline-subtle" ||
												variant === "editor" ||
												variant === "dashed",
											"text-tertiary dark:text-inherit":
												variant === "ghost" ||
												variant === "link" ||
												variant === "link-no-hover" ||
												variant === "glass",
											"text-secondary dark:text-inherit": variant === "subtle",
										},
										iconRightClassName,
									)}
									style={iconStyle}
								/>
							)}
						</div>
					)}
					{command && (
						<span
							className={cn(
								"ml-auto text-xs text-tertiary opacity-90",
								isLoading && "opacity-0",
							)}
						>
							{command}
						</span>
					)}
				</button>
			)
		},
	),
)

type LucideIcon = FC<SVGProps<SVGSVGElement>>
